import { z } from 'zod';
import { isAllowedCatalogValue, type CatalogKey } from '../catalog/index.js';

/**
 * Odczyt jest PERMISYWNY, w przeciwieństwie do zapisu.
 *
 * Strefa klienta może mieć wartość ustawioną kiedyś inną drogą, spoza naszego
 * enuma. Gdyby czytnik ją odrzucił, nie zrobilibyśmy snapshotu, więc nie
 * moglibyśmy zaaplikować — narzędzie padłoby dokładnie na tych zabałaganionych
 * strefach, dla których powstało. Dlatego zwracamy `{ known, raw }`
 * i przechowujemy `raw`.
 */
export type ReadValue = {
  /** Czy wartość mieści się w naszym katalogu. `false` nie jest błędem. */
  readonly known: boolean;
  readonly raw: unknown;
};

export function parseSnapshotValue(key: CatalogKey, raw: unknown): ReadValue {
  return { known: isAllowedCatalogValue(key, raw), raw };
}

/** Odpowiedź `GET /zones/{id}/settings/{setting_id}`. */
export const zoneSettingSnapshotSchema = z.object({
  id: z.string(),
  value: z.unknown(),
  /** Autorytatywna informacja, czy plan strefy pozwala to zmienić. */
  editable: z.boolean().optional(),
  modified_on: z.string().nullish(),
});
export type ZoneSettingSnapshot = z.infer<typeof zoneSettingSnapshotSchema>;

/** Reguła w entrypoint rulesecie. `ref` jest naszym uchwytem do idempotencji. */
export const ruleSnapshotSchema = z.looseObject({
  id: z.string(),
  ref: z.string().optional(),
  description: z.string().optional(),
  expression: z.string().optional(),
  action: z.string().optional(),
  enabled: z.boolean().optional(),
  action_parameters: z.unknown().optional(),
});
export type RuleSnapshot = z.infer<typeof ruleSnapshotSchema>;

export const rulesetSnapshotSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  phase: z.string(),
  kind: z.string().optional(),
  version: z.string().optional(),
  rules: z.array(ruleSnapshotSchema).default([]),
});
export type RulesetSnapshot = z.infer<typeof rulesetSnapshotSchema>;

export const zoneSnapshotSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
  plan: z.string(),
});

/**
 * Pełny stan strefy sprzed zmian. Bez tego nie ma rollbacku, więc powstaje
 * ZANIM cokolwiek wyślemy do Cloudflare.
 */
export const snapshotSchema = z.object({
  takenAt: z.string(),
  zone: zoneSnapshotSchema,
  /** Klucz katalogu → odczytane ustawienie. Per-setting, bo batch API jest deprecated. */
  settings: z.record(z.string(), zoneSettingSnapshotSchema),
  /** Cały obiekt `GET /zones/{id}/bot_management` — PUT go nadpisuje, więc trzymamy całość. */
  botManagement: z.unknown().optional(),
  /** Entrypointy obu faz. `null` oznacza, że entrypoint nie istniał (404). */
  entrypoints: z.record(z.string(), rulesetSnapshotSchema.nullable()),
});
export type Snapshot = z.infer<typeof snapshotSchema>;
